/**
 * ModeTwoPrimitives — the design grammar for the organiser-facing
 * operational surfaces (Dashboard, EventPage, Curator's Desk, Setup
 * folios).
 *
 * Mode 2 is "functional editorial". Per Craig's lock:
 *   • charcoal/graphite foundations (NOT walnut)
 *   • restrained gold accent (the *only* accent — no cyan, no violet)
 *   • parchment-edged panels, hairline dividers, editorial typography
 *   • layered depth via subtle inset shadows + tiny grain
 *   • COMPOSED, NOT cinematic — less motion than the homepage,
 *     less texture than the archive, less theatricality than checkout
 *
 * Reference energy: museum back office / projection booth / luxury
 * hotel operations desk / curator workspace.
 *
 * Anti-reference: Notion clone / SaaS metrics panel / analytics
 * startup / admin CMS.
 *
 * Every primary action uses the same tactile press language as Mode 1
 * — onPointerDown settles inward (translateY +1px) and softens the
 * gold border. No hover-lift, no glow expansion.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// ── Mode 2 tokens ──────────────────────────────────────────────────
export const MODE_TWO = {
  bg:           '#0F1014',
  bgRaised:     '#15161B',
  bgPanel:      '#1B1C22',
  bgPanelHi:    '#22232A',
  // Single accent — restrained gold. Used sparingly.
  gold:         '#B89762',
  goldSoft:     'rgba(184,151,98,0.62)',
  goldMute:     'rgba(184,151,98,0.32)',
  goldHair:     'rgba(184,151,98,0.18)',
  goldPressed:  'rgba(184,151,98,0.10)',
  // Ink — operational copy.
  ink:          '#E6E2D6',         // primary editorial type
  inkMid:       'rgba(230,226,214,0.72)',
  inkMuted:     'rgba(230,226,214,0.48)',
  inkFaint:     'rgba(230,226,214,0.28)',
  hair:         'rgba(230,226,214,0.10)',
  hairSoft:     'rgba(230,226,214,0.06)',
  // The serif used for editorial type. Sans is reserved for tabular
  // operational metadata (dates, counts) so the eye separates
  // "story" from "instrument".
  serif:        'Georgia, "Times New Roman", serif',
  sans:         '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
  mono:         '"SFMono-Regular", "Menlo", monospace',
};

// ── State language — the operational vocabulary ───────────────────
//
// Each event state has a verb (the noun the host reads) and a glyph
// that signals at a glance. The glyph is a ~9px disc; colour shifts
// across the state ladder. NEVER a check-mark, NEVER a coloured pill.
export const EVENT_STATES = {
  preparing:   { label: 'Preparing',           tone: 'rgba(184,151,98,0.42)', sub: 'awaiting commission' },
  settling:    { label: 'Settling in',         tone: 'rgba(184,151,98,0.62)', sub: 'before the night' },
  gathering:   { label: 'Gathering',           tone: 'rgba(220,178,108,0.88)', sub: 'the room is alive' },
  returning:   { label: 'Returning',           tone: 'rgba(212,170,82,0.72)', sub: 'memories still arriving' },
  resting:     { label: 'Resting on the shelf', tone: 'rgba(184,151,98,0.42)', sub: 'on the archive shelf' },
  preserved:   { label: 'Preserved',            tone: 'rgba(184,151,98,0.32)', sub: 'in the archive' },
};


/**
 * Derive an event's state from the canonical fields the dashboard
 * already loads. Pure function — never reads time outside the
 * passed-in `now` so tests can pin the clock.
 */
export function deriveEventState(event, now = new Date()) {
  if (!event) return 'preparing';
  const paid = event.payment_status === 'paid';
  if (!paid) return 'preparing';
  // event_date is "YYYY-MM-DD"
  const eventDate = event.event_date ? new Date(`${event.event_date}T00:00:00`) : null;
  if (!eventDate || isNaN(eventDate.getTime())) return 'settling';
  const msPerDay = 24 * 60 * 60 * 1000;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((eventDate.getTime() - today.getTime()) / msPerDay);
  if (diff > 0) return 'settling';
  if (diff === 0) return 'gathering';
  if (diff >= -7) return 'returning';
  if (diff >= -30) return 'resting';
  return 'preserved';
}


// ── Atoms ──────────────────────────────────────────────────────────

export const PageShell = ({ children, testId = 'mode-two-page' }) => (
  <div
    data-testid={testId}
    style={{
      minHeight: '100vh',
      background: `radial-gradient(ellipse at top, ${MODE_TWO.bgRaised} 0%, ${MODE_TWO.bg} 70%)`,
      color: MODE_TWO.ink,
      fontFamily: MODE_TWO.sans,
      // Prevent any single overflowing child from causing horizontal
      // scroll on mobile. Craig flagged 2026-05-13 — the Dashboard
      // ledger grid pushed the page wider than the viewport.
      overflowX: 'hidden',
    }}
  >
    <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@500&display=swap" rel="stylesheet" />
    {/* Mode 2 responsive primitives — single source for breakpoint
        behaviour. The .mode2-ledger-row class collapses the
        Dashboard's 4-column grid into a single-column stack below
        720px so the row remains composed on phones without losing
        the desktop register rhythm. */}
    <style>{`
      @media (max-width: 720px) {
        .mode2-ledger-row {
          grid-template-columns: 1fr !important;
          align-items: flex-start !important;
          gap: 12px !important;
        }
        .mode2-ledger-row > * {
          width: 100%;
        }
      }
    `}</style>
    {children}
  </div>
);

export const PageGutter = ({ children, max = 1120 }) => (
  <div style={{
    maxWidth: max,
    margin: '0 auto',
    padding: 'clamp(24px, 3.5vw, 44px) clamp(20px, 4vw, 36px) 56px',
  }}>{children}</div>
);

export const Eyebrow = ({ children, tone = MODE_TWO.gold }) => (
  <div style={{
    fontFamily: MODE_TWO.serif, fontStyle: 'italic',
    fontSize: 10.5, letterSpacing: '0.26em',
    textTransform: 'uppercase', color: tone,
  }}>{children}</div>
);

export const EditorialHeading = ({ children, size = 'h1' }) => {
  const sized = {
    h1: 'clamp(28px, 3.4vw, 38px)',
    h2: 'clamp(20px, 2.4vw, 26px)',
    h3: 'clamp(16px, 1.8vw, 19px)',
  }[size] || 'clamp(28px, 3.4vw, 38px)';
  return (
    <div style={{
      fontFamily: MODE_TWO.serif, fontWeight: 500,
      fontSize: sized, color: MODE_TWO.ink,
      letterSpacing: '-0.005em', lineHeight: 1.16,
    }}>{children}</div>
  );
};

export const RestrainedMeta = ({ children, testId }) => (
  <div data-testid={testId} style={{
    fontFamily: MODE_TWO.sans,
    fontSize: 11.5, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: MODE_TWO.inkMuted,
  }}>{children}</div>
);

export const MutedDivider = ({ width, vertical = false, tone = MODE_TWO.goldHair }) => (
  <div aria-hidden style={vertical ? {
    width: 1, height: '100%', background: tone,
  } : {
    width: width || '100%', height: 1, background: tone,
    margin: '12px 0 14px',
  }} />
);

export const GraphitePanel = ({ children, testId, padding = 'clamp(20px, 2.2vw, 28px)', style }) => (
  <div data-testid={testId} style={{
    position: 'relative',
    background: MODE_TWO.bgPanel,
    border: `1px solid ${MODE_TWO.hair}`,
    borderRadius: 6,
    padding,
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.4)`,
    ...style,
  }}>{children}</div>
);

/**
 * StateGlyph — a small operational disc that signals an event's
 * state. Calm, never animated, never a checkmark.
 */
export const StateGlyph = ({ state = 'preparing', size = 9, testId }) => {
  const s = EVENT_STATES[state] || EVENT_STATES.preparing;
  return (
    <span
      aria-label={s.label}
      data-state={state}
      data-testid={testId}
      style={{
        display: 'inline-block',
        width: size, height: size, borderRadius: '50%',
        background: s.tone,
        boxShadow: state === 'gathering' ? '0 0 8px rgba(220,178,108,0.42)' : 'none',
        flexShrink: 0,
      }}
    />
  );
};

export const StateLabel = ({ state = 'preparing', testId }) => {
  const s = EVENT_STATES[state] || EVENT_STATES.preparing;
  return (
    <span data-testid={testId} data-state={state} style={{
      fontFamily: MODE_TWO.serif, fontStyle: 'italic',
      fontSize: 13, color: MODE_TWO.inkMid, letterSpacing: '0.01em',
    }}>{s.label} <span style={{ color: MODE_TWO.gold }}>✦</span></span>
  );
};

// ── QuietPress — Mode 2 primary action ────────────────────────────
//
// Uses the same authored "press" language as Mode 1 GhostPill:
//   • onPointerDown settles inward (translateY +1px)
//   • softens the gold border
//   • NO hover-lift, NO glow expansion, NO colour brightening
export const QuietPress = ({ to, href, onClick, children, testId, variant = 'primary', style, ...rest }) => {
  const [pressed, setPressed] = useState(false);
  const press = () => setPressed(true);
  const release = () => setPressed(false);
  const C = to ? Link : (href ? 'a' : 'button');
  const props = to ? { to } : (href ? { href } : { type: 'button' });
  const base = variant === 'quiet' ? {
    h: 36, pad: '0 14px', bg: 'transparent',
    border: MODE_TWO.goldHair, pressedBorder: MODE_TWO.goldPressed,
    color: MODE_TWO.inkMid, fs: 12.5,
  } : {
    h: 42, pad: '0 18px', bg: MODE_TWO.bgPanelHi,
    border: MODE_TWO.goldMute, pressedBorder: MODE_TWO.goldPressed,
    color: MODE_TWO.ink, fs: 13.5,
  };
  return (
    <C
      ref={null}
      {...props}
      {...rest}
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      onClick={onClick}
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: base.h, padding: base.pad, borderRadius: 4,
        background: base.bg,
        border: `1px solid ${pressed ? base.pressedBorder : base.border}`,
        color: base.color,
        fontFamily: MODE_TWO.serif, fontStyle: 'italic',
        fontSize: base.fs, letterSpacing: '0.01em',
        textDecoration: 'none', cursor: 'pointer',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease',
        ...style,
      }}
    >{children}</C>
  );
};


// ── Editorial phrasing — converts raw counts into restrained sentences
//
// Per Craig: keep the data, remove the dashboard energy. The phrase
// is the headline; tiny operational metadata sits beneath it. The
// system functions; it just doesn't feel optimised-for-engagement.
export function editorialPhrase(stats = {}) {
  const photos   = stats.photos   || 0;
  const videos   = stats.videos   || 0;
  const messages = stats.messages || 0;
  const guests   = stats.guests   || 0;
  const shares   = stats.shares   || 0;
  const total    = photos + videos + messages;

  if (total === 0 && guests === 0 && shares === 0) return null;

  // Multi-axis observation — the platform reports the strongest signal.
  if (shares >= 4 && total >= 20) {
    return 'Letters from this night are still being revisited ✦';
  }
  if (guests >= 8 && total >= 20) {
    return 'The room gathered quickly ✦';
  }
  if (total >= 40) {
    return 'The archive continues to grow ✦';
  }
  if (total >= 12) {
    return 'The wall is filling out gently ✦';
  }
  if (total >= 1) {
    return 'The first memories have settled in ✦';
  }
  if (guests >= 1) {
    return 'Someone has reached in ✦';
  }
  return 'The room is still being prepared ✦';
}


// ── Tabular operational meta — restrained, secondary hierarchy ─────
//
// Render the underlying counts as a single calm line beneath the
// editorial phrase. No icons, no badges, no progress.
export function operationalLine(stats = {}) {
  const photos   = stats.photos   || 0;
  const videos   = stats.videos   || 0;
  const messages = stats.messages || 0;
  const guests   = stats.guests   || 0;
  const parts = [];
  if (photos)   parts.push(`${photos} snap${photos === 1 ? '' : 's'}`);
  if (videos)   parts.push(`${videos} reel${videos === 1 ? '' : 's'}`);
  if (messages) parts.push(`${messages} message${messages === 1 ? '' : 's'}`);
  if (guests)   parts.push(`${guests} guest${guests === 1 ? '' : 's'}`);
  return parts.join(' · ');
}
